import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
} from 'react'
import clsx from 'clsx'
import { VIEW_PARAMETER_DROPDOWN_SCROLLBAR_GUTTER_CLASS } from './compactDropdownStyles'

interface PopoverScrollAreaProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Classes applied only while the area overflows. The default negative
   * gutter is ONLY for menus whose frame carries `p-1`: it pulls the
   * scrollbar back out to the frame edge. Flush field-style frames (no
   * frame padding) must pass '' — their track already spans edge to edge —
   * and keep option padding on an inner wrapper INSIDE the scroll content,
   * never on this container, so the track runs the full height from the
   * search divider (or top) to the bottom with no insets.
   */
  scrollbarGutterClassName?: string
}

const PopoverScrollArea = forwardRef<HTMLDivElement, PopoverScrollAreaProps>(
  (
    {
      className,
      scrollbarGutterClassName = VIEW_PARAMETER_DROPDOWN_SCROLLBAR_GUTTER_CLASS,
      ...props
    },
    ref
  ) => {
    const scrollRef = useRef<HTMLDivElement | null>(null)
    const [hasVerticalOverflow, setHasVerticalOverflow] = useState(false)

    useImperativeHandle(ref, () => scrollRef.current as HTMLDivElement, [])

    useLayoutEffect(() => {
      const node = scrollRef.current
      if (!node) return

      let frameId = 0
      const updateOverflow = () => {
        cancelAnimationFrame(frameId)
        frameId = requestAnimationFrame(() => {
          const nextHasOverflow = node.scrollHeight > node.clientHeight + 1
          setHasVerticalOverflow((current) =>
            current === nextHasOverflow ? current : nextHasOverflow
          )
        })
      }

      updateOverflow()

      const resizeObserver =
        typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateOverflow)
      resizeObserver?.observe(node)

      const mutationObserver =
        typeof MutationObserver === 'undefined' ? null : new MutationObserver(updateOverflow)
      mutationObserver?.observe(node, { childList: true, subtree: true, characterData: true })

      window.addEventListener('resize', updateOverflow)
      return () => {
        cancelAnimationFrame(frameId)
        resizeObserver?.disconnect()
        mutationObserver?.disconnect()
        window.removeEventListener('resize', updateOverflow)
      }
    })

    return (
      <div
        ref={scrollRef}
        className={clsx(
          'popover-selector-scrollbar min-h-0 overflow-y-auto',
          className,
          hasVerticalOverflow && scrollbarGutterClassName
        )}
        {...props}
      />
    )
  }
)

PopoverScrollArea.displayName = 'PopoverScrollArea'

export default PopoverScrollArea
